import { useRef, useEffect, useState, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import skLocale from '@fullcalendar/core/locales/sk';
import { mapBlockedTimesToEvents, getServiceColor } from '../utils/calendar';
import { formatPhoneNumber } from '../utils/formatters';
import './ReservationCalendar.css';

const ReservationCalendar = ({
  reservations,
  services,
  blockedTimes = [],
  onEventClick,
  onDateSelect,
  onEventDrop,
  calendarView = localStorage.getItem('default_calendar_view') || 'timeGridDay',
  slotMinTime = '07:00:00',
  slotMaxTime = '20:00:00',
  workingHours = []
}) => {
  const calendarRef = useRef(null);
  const [currentView, setCurrentView] = useState(calendarView);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const businessHours = useMemo(() => {
    const normalizedHours = (workingHours || [])
      .filter((day) => !day?.zatvorene && day?.cas_od && day?.cas_do)
      .map((day) => ({
        daysOfWeek: [Number(day.den_v_tyzdni)],
        startTime: day.cas_od.slice(0, 5),
        endTime: day.cas_do.slice(0, 5)
      }))
      .filter((day) => !Number.isNaN(day.daysOfWeek[0]));

    if (normalizedHours.length > 0) {
      return normalizedHours;
    }

    return {
      daysOfWeek: [1, 2, 3, 4, 5],
      startTime: '07:00',
      endTime: '20:00'
    };
  }, [workingHours]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      if (mobile && calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        const currentViewType = calendarApi.view.type;
        
        if (currentViewType === 'timeGridWeek') {
          calendarApi.changeView('timeGridDay');
          setCurrentView('timeGridDay');
        }
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Farby stavov
  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
      case 'completed':
        return '#10b981'; // Zelená
      case 'pending':
        return '#f59e0b'; // Oranžová
      case 'cancelled':
        return '#ef4444'; // Červená
      default:
        return '#6b7280'; // Šedá
    }
  };

  const events = [
    ...reservations.map(reservation => {
    const service = services.find(s => s.id === reservation.sluzba_id);
    const serviceName = service ? service.nazov : 'Neznáma služba';
    const duration = service ? service.trvanie_minuty : 60;

    const startDateTime = new Date(`${reservation.datum_rezervacie}T${reservation.cas_rezervacie}`);
    const endDateTime = new Date(startDateTime.getTime() + duration * 60000);

    const serviceColor = getServiceColor(serviceName);
    
    const statusColor = getStatusColor(reservation.stav);

    const now = new Date();
    const reservationEnd = new Date(endDateTime);
    let timeStatus = 'upcoming';
    
    if (now > reservationEnd) {
      timeStatus = 'past';
    } else if (now >= startDateTime && now <= reservationEnd) {
      timeStatus = 'current';
    }

    return {
      id: reservation.id,
      title: serviceName,
      start: startDateTime,
      end: endDateTime,
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      textColor: '#111827',
      extendedProps: {
        customerName: reservation.cele_meno,
        phone: reservation.telefon,
        email: reservation.email,
        address: reservation.adresa,
        status: reservation.stav,
        service: serviceName,
        duration: duration,
        reservationData: reservation,
        serviceColorBg: serviceColor.bg,
        serviceColorText: serviceColor.text,
        statusColor: statusColor,
        timeStatus: timeStatus
      },
      classNames: [
        'custom-event',
        reservation.stav === 'pending' ? 'status-pending' : 
        reservation.stav === 'confirmed' ? 'status-confirmed' : 
        reservation.stav === 'cancelled' ? 'status-cancelled' : 'status-confirmed',
        `time-${timeStatus}`
      ]
    };
  }),
  ...(currentView === 'listWeek'
    ? []
    : mapBlockedTimesToEvents(blockedTimes, {
        slotMinTime,
        slotMaxTime
      }))
];

  const renderEventContent = (eventInfo) => {
    const isListView = eventInfo.view.type === 'listWeek';
    
    if (isListView) {
      const { customerName, phone, email, address } = eventInfo.event.extendedProps;
      const timeText = eventInfo.timeText;
      
      return (
        <div className="agenda-card-content">
          <div className="agenda-card-info">
            {/* Názov služby s časom na mobile */}
            <div className="agenda-service-name" data-time={timeText}>
              <span>{eventInfo.event.title}</span>
              <span className="agenda-time-mobile">{timeText}</span>
            </div>
            
            {/* Informácie - horizontálne v riadku */}
            <div className="agenda-info-group">
              {/* Meno zákazníka */}
              <div className="agenda-customer-name">
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
                </svg>
                <span>{customerName}</span>
              </div>
              
              {/* Email */}
              <div className="agenda-email">
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z"/>
                </svg>
                <span>{email}</span>
              </div>
              
              {/* Telefón */}
              <div className="agenda-phone">
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path fillRule="evenodd" d="M1.885.511a1.745 1.745 0 0 1 2.61.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"/>
                </svg>
                <span>{formatPhoneNumber(phone)}</span>
              </div>
              
              {/* Adresa */}
              <div className="agenda-address">
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path fillRule="evenodd" d="M8 1a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM4 4a4 4 0 1 1 4.5 3.969V13.5a.5.5 0 0 1-1 0V7.97A4 4 0 0 1 4 3.999z"/>
                </svg>
                <span>{address}</span>
              </div>
            </div>
          </div>
          
          <div className="agenda-quick-actions">
            <button 
              className="agenda-action-btn navigate" 
              onClick={(e) => {
                e.stopPropagation();
                const encodedAddress = encodeURIComponent(address || '');
                window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
              }}
              title="Navigovať"
            >
              <svg viewBox="0 0 16 16" fill="currentColor">
                <path fillRule="evenodd" d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
              </svg>
            </button>
            <button 
              className="agenda-action-btn phone" 
              onClick={(e) => {
                e.stopPropagation();
                window.location.href = `tel:${phone}`;
              }}
              title="Zavolať"
            >
              <svg viewBox="0 0 16 16" fill="currentColor">
                <path fillRule="evenodd" d="M1.885.511a1.745 1.745 0 0 1 2.61.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"/>
              </svg>
            </button>
            <button 
              className="agenda-action-btn edit" 
              onClick={(e) => {
                e.stopPropagation();
                onEventClick(eventInfo.event.extendedProps.reservationData);
              }}
              title="Upraviť"
            >
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.9888 4.28491L19.6405 2.93089C18.4045 1.6897 16.4944 1.6897 15.2584 2.93089L13.0112 5.30042L18.7416 11.055L21.1011 8.68547C21.6629 8.1213 22 7.33145 22 6.54161C22 5.75176 21.5506 4.84908 20.9888 4.28491Z"/>
                <path d="M16.2697 10.9422L11.7753 6.42877L2.89888 15.3427C2.33708 15.9069 2 16.6968 2 17.5994V21.0973C2 21.5487 2.33708 22 2.89888 22H6.49438C7.2809 22 8.06742 21.6615 8.74157 21.0973L17.618 12.1834L16.2697 10.9422Z"/>
              </svg>
            </button>
          </div>
        </div>
      );
    }
    
    if (eventInfo.event.extendedProps.isAllDayBlocking) {
      return (
        <div className="fc-event-main-frame">
          <div className="fc-event-title-container">
            <div className="fc-event-title fc-sticky">
			  <div style={{ fontSize: '0.85em', marginTop: '1px', opacity: 0.9, marginBottom: '1px', fontWeight: 'normal' }}>
                Celý deň
              </div>
              {eventInfo.event.title}
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="fc-event-main-frame">
        <div className="fc-event-time">{eventInfo.timeText}</div>
        <div className="fc-event-title-container">
          <div className="fc-event-title fc-sticky">
            {eventInfo.event.title}
          </div>
        </div>
      </div>
    );
  };

  const handleEventMouseEnter = (info) => {
    if (info.event.extendedProps.isBlocked) {
      return;
    }
    
    const { customerName, phone, address } = info.event.extendedProps;
    
    if (info.el._hideTimeout) {
      clearTimeout(info.el._hideTimeout);
      delete info.el._hideTimeout;
    }
    
    if (info.el._tooltip) return;
    
    const tooltip = document.createElement('div');
    tooltip.className = 'calendar-tooltip';
    
    const encodedAddress = encodeURIComponent(address || '');
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    
    tooltip.innerHTML = `
      <div class="tooltip-row"><strong>Zákazník:</strong> ${customerName}</div>
      <div class="tooltip-row">
        <strong>Telefón:</strong> 
        <a href="tel:${formatPhoneNumber(phone)}" class="tooltip-link">${formatPhoneNumber(phone)}</a>
      </div>
      <div class="tooltip-row">
        <strong>Adresa:</strong> 
        <a href="${mapsUrl}" target="_blank" class="tooltip-link tooltip-maps">
          ${address}
          <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" style="margin-left: 4px; vertical-align: middle;">
            <path d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5z"/>
            <path d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0v-5z"/>
          </svg>
        </a>
      </div>
    `;
    
    tooltip.addEventListener('mouseenter', () => {
      if (info.el._hideTimeout) {
        clearTimeout(info.el._hideTimeout);
        delete info.el._hideTimeout;
      }
    });
    
    tooltip.addEventListener('mouseleave', () => {
      if (info.el._tooltip) {
        info.el._tooltip.remove();
        delete info.el._tooltip;
      }
    });
    
    document.body.appendChild(tooltip);
    
    const rect = info.el.getBoundingClientRect();
    tooltip.style.top = `${rect.top + window.scrollY - tooltip.offsetHeight - 10}px`;
    tooltip.style.left = `${rect.left + window.scrollX}px`;
    
    info.el._tooltip = tooltip;
  };

  const handleEventMouseLeave = (info) => {
    // Pridaj delay pred skrytím - dá čas presunúť kurzor na tooltip
    info.el._hideTimeout = setTimeout(() => {
      if (info.el._tooltip) {
        info.el._tooltip.remove();
        delete info.el._tooltip;
      }
    }, 100); // 100ms delay
  };

  useEffect(() => {
    const updateCurrentTime = () => {
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        calendarApi.updateSize();
      }
    };

    const interval = setInterval(updateCurrentTime, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleFocusMode = () => {
    const now = new Date();
    const calendarApi = calendarRef.current?.getApi();
    
    if (!calendarApi) return;
    
    if (currentView !== 'listWeek') {
      calendarApi.changeView('listWeek');
      setCurrentView('listWeek');
    }
    
    const upcomingEvents = events
      .filter(event => new Date(event.start) >= now)
      .sort((a, b) => new Date(a.start) - new Date(b.start));
    
    if (upcomingEvents.length === 0) {
      alert('Žiadne nadchádzajúce rezervácie');
      return;
    }
    
    const currentEvent = upcomingEvents[0];
    
    document.querySelectorAll('.fc-list-event').forEach(el => {
      el.classList.remove('current-focus');
    });
    
    setTimeout(() => {
      const eventElements = document.querySelectorAll('.fc-list-event');
      eventElements.forEach(el => {
        const eventId = el.getAttribute('data-event-id');
        if (eventId === String(currentEvent.id)) {
          el.classList.add('current-focus');
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }, 300);
  };

  return (
    <div className="reservation-calendar">
      {isMobile && (
        <div className="mobile-view-selector">
          <button 
            className={`mobile-view-btn ${currentView === 'timeGridDay' ? 'active' : ''}`}
            onClick={() => {
              const calendarApi = calendarRef.current?.getApi();
              if (calendarApi) {
                calendarApi.changeView('timeGridDay');
                setCurrentView('timeGridDay');
              }
            }}
          >
            Deň
          </button>
          <button 
            className={`mobile-view-btn ${currentView === 'listWeek' ? 'active' : ''}`}
            onClick={() => {
              const calendarApi = calendarRef.current?.getApi();
              if (calendarApi) {
                calendarApi.changeView('listWeek');
                setCurrentView('listWeek');
              }
            }}
          >
            Agenda
          </button>
        </div>
      )}
      <FullCalendar
        ref={calendarRef}
        plugins={[timeGridPlugin, interactionPlugin, listPlugin]}
        initialView={calendarView}
        locale={skLocale}
        headerToolbar={isMobile ? {
          left: 'prev,next',
          center: 'title',
          right: 'today'
        } : {
          left: 'prev,next today',
          center: 'title',
          right: 'focusMode timeGridDay,timeGridWeek,listWeek'
        }}
        customButtons={{
          focusMode: {
            text: 'Práve teraz',
            click: handleFocusMode
          }
        }}
        buttonText={{
          today: 'Dnes',
          day: 'Deň',
          week: 'Týždeň',
          list: 'Agenda'
        }}
        titleFormat={() => ''} 
        datesSet={(dateInfo) => {
          const titleEl = document.querySelector('.fc-toolbar-title');
          if (!titleEl) return;
          
          if (dateInfo.view.type === 'timeGridDay') {
            const date = dateInfo.view.currentStart;
            const weekday = date.toLocaleDateString('sk-SK', { weekday: 'long' });
            const day = date.getDate();
            const month = date.toLocaleDateString('sk-SK', { month: 'long' });
            const year = date.getFullYear();
            
            titleEl.innerHTML = `<span style="font-weight: 400">${weekday}</span> <strong>${day}. ${month} ${year}</strong>`;
          } else if (dateInfo.view.type === 'timeGridWeek' || dateInfo.view.type === 'listWeek') {
            const startDate = dateInfo.view.currentStart;
            const endDate = new Date(dateInfo.view.currentEnd);
            endDate.setDate(endDate.getDate() - 1);
            
            const startDay = startDate.getDate();
            const startMonth = startDate.toLocaleDateString('sk-SK', { month: 'long' });
            const startYear = startDate.getFullYear();
            const endDay = endDate.getDate();
            const endMonth = endDate.toLocaleDateString('sk-SK', { month: 'long' });
            const endYear = endDate.getFullYear();
            
            if (startYear !== endYear) {
              titleEl.innerHTML = `<strong>${startDay}. ${startMonth} ${startYear} – ${endDay}. ${endMonth} ${endYear}</strong>`;
            } else {
              titleEl.innerHTML = `<strong>${startDay}. ${startMonth} – ${endDay}. ${endMonth} ${endYear}</strong>`;
            }
          }
        }}
        viewDidMount={(info) => {
          setCurrentView(info.view.type);
        }}
        slotMinTime={slotMinTime}
        slotMaxTime={slotMaxTime}
        slotDuration="00:30:00"
        slotLabelInterval="01:00"
        slotLabelFormat={{
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }}
        eventTimeFormat={{
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }}
        height="auto"
        allDaySlot={false}
        nowIndicator={true}
        selectable={true}
        selectMirror={true}
        editable={true}
        eventResizableFromStart={true}
        events={events}
        eventContent={renderEventContent}
        noEventsContent={() => (
          <div className="agenda-empty-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <p>Zatiaľ žiadne rezervácie</p>
          </div>
        )}
        eventClick={(info) => {
          info.jsEvent.preventDefault();
          if (info.event.extendedProps.isBlocked) {
            return;
          }
          onEventClick(info.event.extendedProps.reservationData);
        }}
        select={(info) => {
          onDateSelect(info);
        }}
        eventDrop={(info) => {
          if (onEventDrop) {
            const newStart = info.event.start;
            const newDate = newStart.toISOString().split('T')[0];
            const newTime = newStart.toTimeString().slice(0, 5);
            onEventDrop(info.event.extendedProps.reservationData, newDate, newTime);
          }
        }}
        eventMouseEnter={(info) => {
          if (info.view.type !== 'listWeek') {
            handleEventMouseEnter(info);
          }
        }}
        eventMouseLeave={(info) => {
          if (info.view.type !== 'listWeek') {
            handleEventMouseLeave(info);
          }
        }}
        eventDidMount={(info) => {
          info.el.setAttribute('data-event-id', info.event.id);
        }}
        businessHours={businessHours}
        weekends={true}
        eventOverlap={false}
        slotEventOverlap={false}
      />
    </div>
  );
};

export default ReservationCalendar;
